import React, { useEffect, useState, FormEvent, useContext, useRef } from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "../../useTranslation";
import {
  PasswordPolicyInput,
  PreloaderSubmitButton,
  PreloaderButtonState,
  PolicyInputValidState,
  Requirement,
  RequiredInput
} from "@monorepo/components";

import { Auth0Error, Captcha } from "auth0-js";
import { Typography, StandardForm, GridContainer, Row, Column, Box, Checkbox } from "@vp/swan";
import { useAuthenticationContext } from "../AuthenticationContext";
import {
  EmailPreference
} from "../Components/EmailPreferences";
import EmailInput from "../Components/EmailInput";
import { SignAuth0Context } from "../../Auth0Context";
import { useNotificationContext } from "../../NotificationContext";
import TermsAndConditions from "../Components/TermsAndConditions";
import show from "../show.svg";
import hide from "../hide.svg";

import { pushDataToSegment, saveLastAttemptInfo } from "../../segmentData";
import { ACCOUNT_CREATION_ATTEMPT, ACCOUNT_CREATION_PAGE, EU_EXPERIMENT_MAP, EXPERIMENT_MAP, SITE_VCS } from "../../constant";
import { Screens } from "../ScreensLayout";

interface SignUpProps {
  notificationRef?: React.RefObject<HTMLDivElement>;
  setShowSocialSignIn: Function,
  wauth: any,
  onChangeScreen: Function,
  setShowLoginWithEmail: Function,
  isAutomaticAgreementEnabled: boolean
}

const SignUpForm = (props: SignUpProps) => {
  const { notificationRef, setShowSocialSignIn, wauth, onChangeScreen, setShowLoginWithEmail, isAutomaticAgreementEnabled } = props;
  const [t] = useTranslation("signUp");

  const myRef = useRef(null);

  const passwordMinLength: number = +(process.env
    .REACT_APP_PASSWORD_MIN_LENGTH as string);

  const {
    contextEmail,
    setContextEmail,
    validateEmail
  } = useAuthenticationContext();
  const { signUpFailed, invalidFormSubmitted } = useNotificationContext();
  const auth0 = useContext(SignAuth0Context);
  const [email, setEmail] = useState({ value: contextEmail || wauth?.enforcedEmail || "", isValid: true });
  const [password, setPassword] = useState({ value: "", isValid: false });
  const [optOut, setOptOut] = useState(false);
  const [firstName, setFirstName] = useState({ value: "", isValid: false });
  const [lastName, setLastName] = useState({ value: "", isValid: false });
  const [submitState, setSubmitState] = useState(PreloaderButtonState.Disabled);
  const [captcha, setCaptcha] = useState<Captcha>();

  // const {
  //   isAutomaticAgreementEnabled,
  //   isEligibleForSignUpDiscounts,
  //   applicableDiscountOnSignUp
  // } = useCustomization((wauth || {}).site || '');

  useEffect(() => {
    if (myRef.current && auth0.renderCaptcha) {
      setCaptcha(auth0.renderCaptcha(
        document.querySelector(".captcha-container") as HTMLElement, { lang: wauth?.culture }
      ));
    }
  }, [auth0, wauth]);

  useEffect(() => {
    if (email.value !== "" || password.value !== "" || firstName.value !== "" || lastName.value !== "") {
      setShowSocialSignIn(false);
      setShowLoginWithEmail(false);
    }
  }, [email, password, firstName, lastName, setShowSocialSignIn, setShowLoginWithEmail]);

  const [acceptedTermsAndPolicy, setAcceptedTermsAndPolicy] = useState(
    true
    // isAutomaticAgreementEnabled || isEligibleForSignUpDiscounts
  );
  useEffect(() => {
    if (validateEmail(contextEmail)) {
      setEmail({ value: contextEmail, isValid: true });
    }
  }, [contextEmail, validateEmail]);

  const onEmailChanged = (email: string, isValid: boolean): void => {
    if (isValid) {
      setContextEmail(email);
    }

    setEmail({ value: email, isValid });
  };

  const onPasswordChanged = (password: string, isValid: boolean): void => {
    setPassword({ value: password, isValid });
  };

  const switchedToSignIn = () => {
    onChangeScreen(Screens.SignIn);
  };

  const signUpCallback = (err: Auth0Error | null): void => {
    if (err) {
      if (err.code) {
        signUpFailed(err.description === "signup_from_restricted_region"
          ? err.description
          : err.code, { email: email.value }, switchedToSignIn);
      } else {
        signUpFailed();
      }

      captcha?.reload(() => {
        console.log("captcha reloaded");
      });

      if (notificationRef && notificationRef.current) {
        notificationRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }

    setSubmitState(PreloaderButtonState.Enabled);
  };

  const onSignUpSubmit = (e: FormEvent): void => {
    e.preventDefault();
    saveLastAttemptInfo({ label: "Auth0" });
    if ((
      (EXPERIMENT_MAP.get(((wauth || {}).site || "").toLowerCase()) || []).includes((wauth || {}).culture) ||
      (EU_EXPERIMENT_MAP.get(((wauth || {}).site || "").toLowerCase()) || []).includes((wauth || {}).culture)
    ) &&
       isAutomaticAgreementEnabled) {
      pushDataToSegment(ACCOUNT_CREATION_ATTEMPT, false, { label: "Auth0", pageName: ACCOUNT_CREATION_PAGE, experiment_name: `${(wauth || {}).culture}-randomized-email-preference-opt-in`, experiment_value: "true" });
    } else {
      pushDataToSegment(ACCOUNT_CREATION_ATTEMPT, false, { label: "Auth0", pageName: ACCOUNT_CREATION_PAGE });
    }

    if (submitState !== PreloaderButtonState.Enabled) {
      invalidFormSubmitted(ACCOUNT_CREATION_PAGE);
    } else {
      setSubmitState(PreloaderButtonState.Preloader);
      auth0.signUp(
        email.value,
        password.value,
        firstName.value,
        lastName.value,
        isAutomaticAgreementEnabled ? (optOut ? EmailPreference.No : EmailPreference.All) : EmailPreference.None,
        wauth.culture,
        wauth.site,
        signUpCallback,
        captcha?.getValue()
      );
    }
  };

  const handleFirstNameChange = (newValue: string, isValid: boolean) => {
    setFirstName({ value: newValue, isValid });
  };

  const handleFirstNameErrorMessages = (validity: string): string => {
    if (validity !== PolicyInputValidState) {
      return t("firstName.missing");
    }

    return "";
  };

  const handleLastNameChange = (newValue: string, isValid: boolean) => {
    setLastName({ value: newValue, isValid });
  };

  const handleLastNameErrorMessages = (validity: string): string => {
    if (validity !== PolicyInputValidState) {
      return t("lastName.missing");
    }

    return "";
  };

  useEffect(() => {
    if (
      email.isValid &&
      password.isValid &&
      firstName.isValid &&
      lastName.isValid &&
      acceptedTermsAndPolicy
    ) {
      setSubmitState(PreloaderButtonState.Enabled);
    } else {
      setSubmitState(PreloaderButtonState.Disabled);
    }
  }, [
    email.isValid,
    password.isValid,
    firstName.isValid,
    lastName.isValid,
    acceptedTermsAndPolicy
  ]);

  const translateMessages = (text: string, options: any): string => {
    if (!options) {
      return t(`app:${text}`);
    }
    return t(`app:${text}`, options);
  };

  const showTermsAndConditionsAgreement = () => {
    const termsAndConditionsSection = (
      <TermsAndConditions
        site={((wauth || {}).site || "").toLowerCase()}
        culture={((wauth || {}).culture || "")}
        accepted={acceptedTermsAndPolicy}
        onAcceptanceChanged={setAcceptedTermsAndPolicy}
        disableSelection={true}
      />
    );

    if (isAutomaticAgreementEnabled) {
      const automaticAgreement = `signUp:exclusiveEmails.${((wauth || {}).site || "").toLowerCase()}automaticAgreement`;
      return wauth.culture === "en-IN" && ((wauth || {}).site || "").toLowerCase() === "vistaprint"
        ? null
        : (
        <>
          <Box mb={4}>
            <Typography
              fontSize="xsmall"
              fontSkin="footnote"
              data-testid="automaticAgreement"
            >
              <Trans i18nKey={automaticAgreement}>
                <a href={t("termsAndPrivacy.privacyPolicyLink")} />
              </Trans>
            </Typography>
          </Box>
          {/* { (EU_EXPERIMENT_MAP.get(((wauth || {}).site || '').toLowerCase())|| []).includes((wauth || {}).culture) &&
          <>
                <Typography my="4" fontSize={-1} data-testid="optOutTerms">
                <Checkbox marginRight={'2'} onChange={() => setOptOut(!optOut)} />
                <Trans i18nKey="signUp:termsAndPrivacy.optOutTerms">
                </Trans>
              </Typography>
          </>

          } */}
          {termsAndConditionsSection}
        </>
          );
    }
    return (
      <>
        {termsAndConditionsSection}
      </>
    );
  };

  const showVCSSignUpInstructions = () => {
    if (wauth.site === SITE_VCS) {
      return (
        <Box mb={4}>
          <Typography fontSize="xsmall" fontSkin="footnote">
            {t("vcsSignUpInstructions")}
          </Typography>
        </Box>
      );
    } else {
      return null;
    }
  };

  const showOptOutCheckBox = () => {
    if (["en-gb", "es-es", "de-ch", "fr-ch", "it-ch"].includes(wauth?.culture?.toLowerCase())) {
      return (
        <Box mt={4}>
          <Typography
            fontSize="xsmall"
            data-testid="optOutTerms"
            className="align-items-center"
          >
            <Checkbox marginRight={3} onChange={() => { setOptOut(!optOut); }} />
            <Trans i18nKey="signUp:termsAndPrivacy.optOutTerms"></Trans>
          </Typography>
        </Box>
      );
    } else {
      return null;
    }
  };
  return (
    <Box mb="to-actions">
      <StandardForm
        id="signup-form"
        skin="tight"
        method="post"
        onSubmit={onSignUpSubmit}
        noValidate
      >
        <Box mb="between-subsections">
          <GridContainer>
            <Row>
              <Column span={6}>
                <RequiredInput
                  id="firstName"
                  type={"text"}
                  value={firstName.value}
                  onChange={handleFirstNameChange}
                  policyErrorMessage={handleFirstNameErrorMessages}
                  placeholder={t("firstName.placeholder")}
                  required={Requirement.Required}
                />
              </Column>
              <Column span={6}>
                <RequiredInput
                  id="lastName"
                  type={"text"}
                  value={lastName.value}
                  onChange={handleLastNameChange}
                  policyErrorMessage={handleLastNameErrorMessages}
                  placeholder={t("lastName.placeholder")}
                  required={Requirement.Required}
                />
              </Column>
            </Row>
          </GridContainer>
          <EmailInput
            disabled={(wauth?.enforcedEmail && wauth?.disableEmailField)}
            id="sign-up-email"
            email={email.value}
            validateEmail={validateEmail}
            onEmailChanged={onEmailChanged}
            required={Requirement.Required}
            autoComplete={"Email"}
          />
          <PasswordPolicyInput
            id="sign-up-password"
            passwordMinLength={passwordMinLength}
            onPasswordChanged={onPasswordChanged}
            required={Requirement.Required}
            messages={translateMessages}
            placeholder={t("app:password.placeholder")}
            autoComplete={"new-password"}
            passwordVisibilityIcons={{
              show: {
                content: show,
                alt: t("app:password.show"),
                title: t("app:password.show")
              },
              hide: {
                content: hide,
                alt: t("app:password.hide"),
                title: t("app:password.hide")
              }
            }}
          />
        </Box>
        <Box mb="to-actions">
          {showVCSSignUpInstructions()}
          {showTermsAndConditionsAgreement()}
        </Box>
        <Box
          mb="to-actions"
          className="captcha-container"
          ref={myRef}
          style={{ textAlign: "center" }}
        ></Box>
        <PreloaderSubmitButton
          name="submit"
          text={t("submit.text")}
          preloaderText={t("submit.preloaderText")}
          preloaderAccessibilityLabel={t("submit.preloaderAccessibilityLabel")}
          state={submitState}
        />
        {showOptOutCheckBox()}
      </StandardForm>
    </Box>
  );
};

export default SignUpForm;
